import React, { useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Truck } from "lucide-react";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const empty = { name: "", phone: "", email: "", address: "", notes: "" };

export default function Suppliers() {
  const { t } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => { const r = await api.get("/suppliers"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name) { toast.error(t("required_field")); return; }
    try {
      if (editId) await api.put(`/suppliers/${editId}`, form);
      else await api.post("/suppliers", form);
      toast.success(t("saved")); setOpen(false); setForm(empty); setEditId(null); load();
    } catch { toast.error(t("error_generic")); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: t("delete"),
      confirmLabel: t("delete"),
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/suppliers/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-4xl gold-text">{t("nav_suppliers")}</h1>
        <button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-supplier-btn" className="btn-gold px-4 py-2 rounded-md flex items-center gap-2">
          <Plus size={16}/>{t("add")}
        </button>
      </div>

      <div className="card-surface overflow-auto">
        <table className="table-dark">
          <thead><tr><th>{t("name")}</th><th>{t("phone")}</th><th>{t("email")}</th><th>{t("address")}</th><th>{t("actions")}</th></tr></thead>
          <tbody>
            {items.map(s => (
              <tr key={s.supplier_id}>
                <td className="text-[var(--text)] flex items-center gap-2"><Truck size={14} className="text-[var(--gold)]"/>{s.name}</td>
                <td className="text-[var(--text-muted)]" dir="ltr">{s.phone}</td>
                <td className="text-[var(--text-muted)]">{s.email}</td>
                <td className="text-[var(--text-muted)]">{s.address}</td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={() => { setForm(s); setEditId(s.supplier_id); setOpen(true); }} className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)]"><Pencil size={14}/></button>
                    <button onClick={() => remove(s.supplier_id)} className="p-1.5 rounded border border-[var(--border)] hover:border-red-500"><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="text-center text-[var(--text-muted)] py-8">{t("no_data")}</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="card-surface p-6 w-full max-w-lg space-y-4" onMouseDown={onModalPanelMouseDown}>
            <h2 className="font-heading text-2xl gold-text">{editId ? t("edit") : t("add")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)]">{t("name")}</label><input className="input-dark" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="sup-name"/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("phone")}</label><input className="input-dark" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("email")}</label><input className="input-dark" value={form.email} onChange={e => setForm({...form, email: e.target.value})}/></div>
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)]">{t("address")}</label><input className="input-dark" value={form.address} onChange={e => setForm({...form, address: e.target.value})}/></div>
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)]">{t("notes")}</label><textarea className="input-dark" rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}/></div>
            </div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)]" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="sup-save" onClick={submit}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
